import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import Image from "next/image";
import Calendar from "@modules/common/icons/calendar";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "@i18n";

const OneTimeLine = ({
  title,
  time,
  story,
  even,
  image,
}: {
  title: string;
  time: string;
  story: string;
  even?: boolean;
  image: string;
}) => {
  const ref = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const isInView = useInView(ref, {
    once: true,
    margin: isMobile ? "50% 0px -30% 0px" : "50% 0px -30% 0px",
  });

  return (
    <motion.div
      ref={ref}
      animate={{
        height: isInView ? (isMobile ? "70vh" : "50vh") : "0%", // Changed for mobile
        minHeight: isInView ? (isMobile ? "600px" : "50vh") : "0%", // Changed for mobile
        marginTop: isMobile ? "5rem" : "10rem",
        transition: {
          bounce: 0.3,
          duration: 0.6,
        },
      }}
      className="md:h-[50vh] h-[70vh] w-full overflow-y-clip mt-10 relative" // Added explicit height classes
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{
          scale: isInView ? 1 : 0,
          transition: {
            type: "spring",
            delay: 0.2,
            duration: 0.5,
            bounce: 0.3,
          },
        }}
        className="md:p-4 p-2 rounded-full bg-primary text-terryWhite text-2xl font-semibold absolute top-0 md:start-[-2rem] start-[-1.17rem] z-10 transform "
      >
        <Calendar
          size="inherit"
          className="md:h-[20px] md:w-[20px] h-[15x] w-[15px]"
        />
      </motion.div>
      <div
        className={`flex justify-between gap-4 md:gap-0 items-center md:items-start flex-col  h-full md:ms-14 ${
          even ? "md:flex-row" : "md:flex-row-reverse"
        }`}
      >
        <div
          className={`w-full md:mt-2 text-primary ${
            even ? "md:w-[55%] md:ps-0 ps-8" : "ps-8  md:w-[45%]"
          }`}
        >
          <div className="text-3xl font-bold">{time}</div>
          <div className="text-xl mt-6 text-terryWhite font-semibold">
            {title}
          </div>
          <div className="md:text-xl text-base mt-2 text-terryWhite md:w-4/5 text-justify md:text-start">
            {story}
          </div>
        </div>
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{
            delay: 0.3, // Reduce delay for faster response
            duration: 0.3,
            ease: "easeInOut",
          }}
          className={`relative h-full ms-8 md:ms-0 w-[90%] ${
            even ? "md:w-[45%]" : "md:w-[55%]"
          } overflow-hidden`} // Prevents layout shifting
        >
          <div className="relative w-full h-64 md:h-96 overflow-visible">
            <Image
              src={image}
              alt="team"
              fill
              style={{ objectFit: "cover" }}
              className="rounded-3xl"
              objectPosition="top"
              priority // Optimize loading
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

function Journey() {
  const { t } = useTranslation();
  const steps = t.about.journey;
  return (
    <div className="pb-40">
      <h1 className="text-5xl flex w-full justify-center mt-20 md:mt-40 text-primary font-medium font-myFont">
        {t.about.ourJourney}
      </h1>
      <div className="mt-20 relative flex">
        <div className="border-s-2 border-dashed border-primary w-2 h-auto  transform -translate-x-1/2 rtl:translate-x-1/2 transition-all duration-50 min-h-1 start-0 mb-[70vh] md:mb-[50vh]"></div>
        <div className="p-2 md:p-3 rounded-full bg-primary text-terryWhite text-2xl font-semibold absolute top-0 start-0 z-10 transform -translate-x-[56%] rtl:translate-x-[56%]"></div>
        <div>
          {steps.map((step, index) => (
            <OneTimeLine
              key={index}
              title={step.title}
              time={step.date}
              story={step.text}
              even={index % 2 === 0}
              image={step.image}
            />
          ))}
          {/* <OneTimeLine
            title="Launch in Canada "
            time="Early 2022"
            story="Our story began in the bustling tech hub of Toronto, where we launched our MVP in one of the most competitive markets globally. This rigorous pilot, tested against world-class tech providers, laid the foundation for what would become a powerful and adaptable digital loyalty solution."
            even
            image="/Journey-1.png"
          />
          <OneTimeLine
            title="Expansion to Egypt"
            time="Early 2023"
            story="With our sights set on growth, we entered the vibrant and dynamic F&B market in Egypt. Here, we scaled our product beyond its MVP stage, refining its capabilities and establishing ourselves as the premier digital loyalty solution for SMEs in the region."
            image="/Journey-2.png"
          />
          <OneTimeLine
            title="Partnership with Dsquares"
            time="Late 2024"
            story="Our success in Egypt caught the attention of Dsquares, a leading loyalty solutions provider for enterprises. In late 2024, Dsquares acquired a majority stake in Prepit, marking a transformative milestone. This partnership positioned us as their SME-focused sister company, poised for even greater impact."
            even
            image="/Journey-3.png"
          /> */}
          {/* <OneTimeLine
            title="Expansion to Saudi Arabia"
            time="Early 2025"
            story="We kicked off 2025 with a bold move into the Saudi market, one of the region’s most challenging and rewarding landscapes. This expansion reflects our commitment to innovation and our ambition to redefine digital loyalty solutions across the Middle East."
            image="/Journey-4.png"
          /> */}
        </div>
      </div>
    </div>
  );
}

export default Journey;
